import type {ReactElement} from 'react';
import React from 'react';
import {BaseNavOption} from '@Components/poster-editor/components/poster-editor-nav-bar/components/base-nav-option';
import styles from './nav-save-option.module.scss';
import {useAppSelector} from '@/hooks';
import {isEmbeddedMorphModeActive} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.helper';
import {NavRightSideOptionTypes} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-right-options/nav-right-options.types';
import {isInReviewOrGalleryTemplate} from '@Components/poster-editor/library/poster-editor-library';

interface NavSaveOptionProps {
  saveOptionType?: NavRightSideOptionTypes;
}

export function NavSaveOption({saveOptionType = NavRightSideOptionTypes.BUTTON}: NavSaveOptionProps): ReactElement {
  const didPosterRecentlySave = useAppSelector((state) => {
    return state.posterEditor.didPosterRecentlySave;
  });
  const isEmbeddedExportMode = useAppSelector((state) => {
    return state.posterEditor.isEmbeddedExportMode;
  });
  const areImagesUploading = useAppSelector((state) => {
    return !!state.posterEditor.posterObject?.uploadingImages.length;
  });
  const hasUnsavedChanges = useAppSelector((state) => {
    return state.posterEditor.hasUnsavedChanges;
  });

  const getSaveIcon = (): string => {
    if (didPosterRecentlySave) {
      return 'icon-check';
    }

    if (isInReviewOrGalleryTemplate()) {
      return 'icon-submit';
    }

    return 'icon-save';
  };

  const getSaveText = (): string => {
    if (didPosterRecentlySave) {
      return window.i18next.t('pmwjs_saved');
    }

    if (isInReviewOrGalleryTemplate()) {
      return window.i18next.t('pmwjs_save_&_submit');
    }

    return window.i18next.t('pmwjs_save');
  };

  const shouldHideSaveOption = (): boolean => {
    return isEmbeddedExportMode || !!window.posterEditor?.whiteboard?.savePoster.isAutoSaveEnabled() || isEmbeddedMorphModeActive();
  };

  const onSave = (): void => {
    if (didPosterRecentlySave) {
      return;
    }

    void window.posterEditor?.whiteboard?.savePoster.save();
  };

  const getSaveDisabledState = (): boolean => {
    if (didPosterRecentlySave) {
      return false;
    }

    return !hasUnsavedChanges || areImagesUploading;
  };

  const isPosterSaving = useAppSelector((state) => {
    return state.posterEditor.isPosterSaving;
  });

  const showSaveOption = (): ReactElement | null => {
    return shouldHideSaveOption() ? null : (
      <BaseNavOption
        id="nav-save-option"
        icon={getSaveIcon()}
        text={getSaveText()}
        optionType={saveOptionType}
        onClickHandler={(): void => {
          onSave();
        }}
        hasMarginRight
        disabled={getSaveDisabledState()}
        isLoading={isPosterSaving}
        btnClasses={`${styles.saveButton} ${isInReviewOrGalleryTemplate() ? styles.saveAndSubmitButton : ''}`}
      />
    );
  };

  return <>{showSaveOption()}</>;
}
